/**
 * Client-facing hotel + budget cards (PI-3 · Pass 2 P1-A).
 *
 * Renders the enriched hotel structured fields (why_fits, role_in_trip,
 * tradeoff_solved, watch_for, est_price, room_type, included_in_rate,
 * cancellation_policy, recommendation_lane). No internal labels — every
 * caption is client-facing travel language.
 *
 * Also exports `BudgetPlanningRange` — the four-tier client-facing budget
 * planning panel.
 */
import React from "react";
import { MapPin, Bed, Check, AlertCircle, ExternalLink } from "lucide-react";
import { Surface } from "../index";
import SafeImage, { isAdvisorPreviewMode } from "./SafeImage";

const LANE_LABEL = {
  recommended: "Recommended Stay",
  comparison: "Comparison Option",
  splurge: "Signature Direction",
};
const LANE_ACCENT = {
  recommended: "text-[hsl(var(--accent-gold))]",
  comparison: "text-[hsl(var(--foreground-muted))]",
  splurge: "text-[hsl(var(--accent-gold))]",
};

export function HotelCard({ item }) {
  if (!item) return null;
  const lane = item.recommendation_lane || "recommended";
  return (
    <Surface
      variant="paper"
      padded
      className="!p-5 space-y-3"
      data-testid={`client-hotel-card-${item.id}`}
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className={`font-mono text-[10px] uppercase tracking-[0.24em] ${LANE_ACCENT[lane] || LANE_ACCENT.recommended}`}>
            {LANE_LABEL[lane] || "Recommended"}
          </p>
          <h3 className="mt-1 font-serif text-[20px] sm:text-[22px] leading-tight text-[hsl(var(--foreground))]">
            {item.title}
          </h3>
          {(item.city || item.country) && (
            <p className="mt-1 flex items-center gap-1.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
              <MapPin className="size-3" strokeWidth={1.5} />
              {[item.city, item.country].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
        {item.official_website && (
          <a
            href={item.official_website}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] border border-[rgba(0,0,0,0.08)]"
            data-testid={`client-hotel-${item.id}-website`}
          >
            <ExternalLink className="size-3" strokeWidth={1.5} />
            Property page
          </a>
        )}
      </div>

      {(() => {
        const advisorMode = isAdvisorPreviewMode();
        // Client-side: collapse missing images entirely.
        // Advisor preview: show tasteful "Image pending" placeholder.
        if (!item.image_url && !advisorMode) return null;
        return (
          <div className="rounded-md overflow-hidden -mx-1 mt-1">
            <SafeImage
              src={item.image_url}
              alt={item.title || ""}
              mode={advisorMode ? "advisor" : "client"}
              aspect="aspect-[16/8]"
              testId={`client-hotel-${item.id}-image`}
            />
          </div>
        );
      })()}

      {item.client_copy && (
        <p className="font-serif text-[14.5px] leading-relaxed text-[hsl(var(--foreground))]">
          {item.client_copy}
        </p>
      )}

      {/* Enriched structured rows — client-friendly captions */}
      <dl className="space-y-2 pt-1">
        <Row label="Why this property" value={item.why_fits} />
        <Row label="Its role in your trip" value={item.role_in_trip} />
        <Row label="What it solves" value={item.tradeoff_solved} />
        <Row label="Something to watch for" value={item.watch_for} icon={AlertCircle} />
        <Row label="Room type recommended" value={item.room_type} icon={Bed} />
        <Row label="Estimated nightly investment" value={item.est_price} />
        <Row label="What's included" value={item.included_in_rate} icon={Check} />
        <Row label="Cancellation" value={item.cancellation_policy} />
      </dl>
    </Surface>
  );
}

function Row({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline gap-3 py-1 border-b border-[rgba(0,0,0,0.04)] last:border-b-0">
      <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] w-44 shrink-0 flex items-center gap-1.5">
        {Icon && <Icon className="size-3" strokeWidth={1.5} />}
        {label}
      </dt>
      <dd className="font-serif text-[13.5px] leading-relaxed text-[hsl(var(--foreground))] flex-1">
        {value}
      </dd>
    </div>
  );
}

const DEFAULT_BUDGET_TIERS = [
  {
    key: "design_target",
    label: "Design Target",
    range: "$10,000",
    caption: "Where we are aiming, inclusive of flights, hotels, transfers, and two anchor experiences.",
    defaultVisible: true,
  },
  {
    key: "planning_range",
    label: "Planning Range",
    range: "$10,000 – $12,000",
    caption: "The working range I will use while refining hotels, flights, transfers, and the final itinerary direction.",
    defaultVisible: true,
  },
  {
    key: "near_ceiling",
    label: "Near Ceiling",
    range: "$11,500 – $12,000",
    caption: "Advisor buffer — surfaced only if pricing drifts toward the ceiling.",
    defaultVisible: false,
  },
  {
    key: "requires_approval",
    label: "Requires Redesign / Approval",
    range: "Over $12,000",
    caption: "We pause and align before anything pushes the trip beyond the ceiling.",
    defaultVisible: false,
  },
];

export function BudgetPlanningRange({ investment }) {
  const inv = investment || {};
  const visibility = inv.visibility || {};
  // Respect visibility — tiers marked false (or default-false when not set)
  // are completely omitted client-side. No empty cards, no hidden CSS.
  const tiers = DEFAULT_BUDGET_TIERS
    .filter((t) => {
      const v = visibility[t.key];
      if (typeof v === "boolean") return v;
      return t.defaultVisible;
    })
    .map((t) => ({
      ...t,
      range: inv[t.key] || t.range,
      caption: inv[`${t.key}_caption`] || t.caption,
    }));
  if (tiers.length === 0) return null;
  const headline = inv.headline || (
    "Designed around a target investment of approximately $10,000 including flights, with a planning range up to $12,000 to allow for airfare, hotel availability, room category, and final routing decisions."
  );
  return (
    <div className="space-y-5" data-testid="client-budget-planning-range">
      <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))] leading-relaxed">
        {headline}
      </p>
      <div className={`grid grid-cols-1 ${tiers.length > 1 ? "sm:grid-cols-2" : ""} gap-4`}>
        {tiers.map((t) => (
          <div
            key={t.key}
            className="px-5 py-4 rounded-md border border-[rgba(0,0,0,0.06)] bg-white/40 space-y-2"
            data-testid={`client-budget-tier-${t.key}`}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">{t.label}</p>
            <p className="font-serif text-[22px] sm:text-[24px] leading-tight text-[hsl(var(--foreground))]">{t.range}</p>
            <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] leading-relaxed">{t.caption}</p>
          </div>
        ))}
      </div>
      {Array.isArray(inv.included) && inv.included.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="client-budget-included">
          <div className="rounded-md border border-[rgba(0,0,0,0.06)] bg-white/40 p-4 space-y-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">Included</p>
            <ul className="font-serif text-[13.5px] text-[hsl(var(--foreground))] space-y-0.5 list-disc list-inside">
              {inv.included.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
          {Array.isArray(inv.exclusions) && inv.exclusions.length > 0 && (
            <div className="rounded-md border border-[rgba(0,0,0,0.06)] bg-white/40 p-4 space-y-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Not included</p>
              <ul className="font-serif text-[13.5px] text-[hsl(var(--foreground-secondary))] space-y-0.5 list-disc list-inside">
                {inv.exclusions.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      {inv.notes_client && (
        <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] leading-relaxed pt-2 border-t border-[rgba(0,0,0,0.05)]">
          {inv.notes_client}
        </p>
      )}
    </div>
  );
}
